import { useEffect, useRef, useState } from "react";
import { Calculator as CalcIcon, TrendingUp, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

const esNum = (n) => Math.round(n).toLocaleString("es-ES", { useGrouping: "always" });

export default function Calculadora() {
  const [actual, setActual] = useState(5);
  const [margen, setMargen] = useState(1000);
  const [objetivo, setObjetivo] = useState(8);

  // El objetivo nunca puede ser menor que lo que ya vende.
  useEffect(() => {
    if (objetivo < actual) setObjetivo(actual);
  }, [actual]); // eslint-disable-line react-hooks/exhaustive-deps

  const extra = objetivo - actual;
  const mensual = extra > 0 ? extra * margen : 0;
  const anual = mensual * 12;

  const displayMensual = useCountUp(mensual);

  return (
    <section id="calculadora" data-testid="calculator-section" className="relative py-24 lg:py-32 border-t border-white/5">
      <div className="absolute inset-0 bg-radial-blue opacity-50 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <Reveal>
          <div className="max-w-3xl mb-12 text-center mx-auto">
            <div className="font-stencil text-xs text-[#00A0A0] tracking-widest mb-4">06 / HAZ NÚMEROS</div>
            <h2 className="font-heading text-white text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]">
              ¿CUÁNTO MARGEN
              <br />
              <span className="font-stencil text-[#00A0A0]">DEJAS SOBRE LA MESA?</span>
            </h2>
            <p className="mt-6 text-white/60 max-w-xl mx-auto font-body text-base lg:text-lg leading-relaxed">
              Mueve los sliders con tus cifras reales y mira el margen extra que podrías generar cada mes.
            </p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 max-w-5xl mx-auto">
          {/* Sliders */}
          <Reveal className="lg:col-span-7">
            <div className="glass p-6 sm:p-7 lg:p-10 h-full">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#00A0A0]/15 border border-[#00A0A0]/30 flex items-center justify-center">
                  <CalcIcon className="w-5 h-5 text-[#00C8C8]" />
                </div>
                <h3 className="font-stencil text-lg lg:text-xl text-white uppercase tracking-wide">Tus cifras reales</h3>
              </div>

              <div className="space-y-8">
                <Slider
                  testid="slider-actual"
                  label="Coches que vendes ahora al mes"
                  value={actual}
                  min={1}
                  max={50}
                  step={1}
                  onChange={setActual}
                  fmtValue={(v) => v}
                  fmtMin="1"
                  fmtMax="50"
                />
                <Slider
                  testid="slider-margen"
                  label="Margen medio por coche"
                  value={margen}
                  min={200}
                  max={5000}
                  step={50}
                  onChange={setMargen}
                  fmtValue={(v) => `${esNum(v)}€`}
                  fmtMin="200€"
                  fmtMax="5.000€"
                />
                <Slider
                  testid="slider-objetivo"
                  label="Coches objetivo al mes con Fuelkers"
                  value={objetivo}
                  min={actual}
                  max={60}
                  step={1}
                  onChange={setObjetivo}
                  fmtValue={(v) => v}
                  fmtMin={String(actual)}
                  fmtMax="60"
                />
              </div>
            </div>
          </Reveal>

          {/* Resultado */}
          <Reveal delay={120} className="lg:col-span-5">
            <div className="glass p-6 sm:p-7 lg:p-10 h-full relative overflow-hidden flex flex-col justify-center text-center">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#00A0A0]/15 rounded-full blur-3xl" />
              <div className="relative">
                {extra > 0 ? (
                  <div data-testid="calc-result">
                    <div className="flex items-center justify-center gap-2 mb-2 text-xs uppercase tracking-widest text-white/50">
                      <TrendingUp className="w-3.5 h-3.5 text-[#00A0A0]" />
                      Margen extra al mes
                    </div>
                    <div
                      data-testid="calc-monthly-margin"
                      className="font-heading text-[#00A0A0] text-glow leading-none tracking-tight tabular-nums whitespace-nowrap px-1"
                      style={{ fontSize: "clamp(34px, 5.5vw, 56px)" }}
                    >
                      +{esNum(displayMensual)}€
                    </div>
                    <div data-testid="calc-secondary" className="mt-4 text-white font-body text-sm sm:text-base leading-relaxed">
                      <span className="text-white/70">Coches extra/mes:</span> <strong className="text-[#00C8C8]">+{extra}</strong>
                      <span className="mx-2 text-white/40">·</span>
                      <span className="text-white/70">Margen anual:</span> <strong className="text-[#FFD700]">+{esNum(anual)}€</strong>
                    </div>
                  </div>
                ) : (
                  <div data-testid="calc-prompt" className="py-6 font-stencil text-lg sm:text-xl text-white/50 uppercase tracking-wide leading-snug">
                    Sube el objetivo para ver tu margen potencial ↑
                  </div>
                )}

                <a
                  href="#contacto"
                  data-testid="calc-cta"
                  className="mt-8 w-full h-14 btn-angular bg-[#FFD700] hover:bg-[#FFE34D] text-black text-xs sm:text-sm font-stencil uppercase tracking-widest rounded-none group inline-flex items-center justify-center gap-2 transition-colors"
                  style={{ boxShadow: "5px 5px 0 #00A0A0" }}
                >
                  RESERVAR DIAGNÓSTICO GRATUITO
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>

                <p className="mt-5 text-xs text-white/40 font-body leading-relaxed">
                  La inversión y los resultados exactos dependen de tu zona, tu stock y tu punto de partida. Es una estimación para hacer números, no una promesa cerrada.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Slider({ label, value, min, max, step, onChange, fmtValue, fmtMin, fmtMax, testid }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <label className="text-sm text-white/70 font-body">{label}</label>
        <span className="font-heading text-xl text-[#00C8C8] tabular-nums">{fmtValue(value)}</span>
      </div>
      <input
        type="range"
        data-testid={testid}
        className="slider-blue w-full"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ "--value": `${pct}%` }}
      />
      <div className="flex justify-between mt-2 text-[11px] font-body text-white/30">
        <span>{fmtMin}</span>
        <span>{fmtMax}</span>
      </div>
    </div>
  );
}

function useCountUp(target) {
  const [display, setDisplay] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const start = performance.now();
    const dur = 360;
    const step = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      const val = from + (target - from) * e;
      setDisplay(val);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target]);

  return display;
}
